import { forwardRef, useEffect, useImperativeHandle, useState, useMemo, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { api } from '@/api/repository/axios';
import { navigationService } from '@/common/navigationService';
import { ImageTooLargeError } from '@/utils/compressImage';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';

import { designSystem } from '@/styles/theme';

/** 바깥(글쓰기 화면의 도구 줄)에서 부르는 동작 */
export interface BoardHandle {
  /** 사진 고르기 창을 연다 — 도구 막대의 사진 버튼과 같은 동작(형식 검사·압축·업로드 후 본문에 넣기) */
  pickImage: () => void;
  /** 본문 끝에 커서를 둔다 */
  focus: () => void;
}

interface BoardProps {
  onChange: (value: string) => void;
  defaultValue?: string;
  value?: string;
  onError?: (message: string) => void;
  /** 이미지 업로드 용도 — 세미나 설명·채용 상세면 서버가 긴 이미지의 위쪽 파생본(_top.jpg)을 만든다. 다른 곳은 비워 둔다 */
  uploadPurpose?: 'seminar-description' | 'recruitment-description';
  /**
   * 글쓰기 화면(속닥속닥·전문가 라운지, 시안 docs/ui-mockups/post-write)용 — 도구 막대·테두리·안쪽 여백 없이 본문만 그린다.
   * 사진은 바깥 버튼이 `pickImage()` 로 올리고, 본문 안 사진은 오른쪽 위 ✕ 로 뺀다. 다른 화면은 넘기지 않는다(기존 모양 그대로).
   */
  bare?: boolean;
  /** bare 일 때 빈 본문에 보이는 안내 글 — 빈 줄(\n\n)로 문단을 나눈다 */
  placeholder?: string;
  /** bare 일 때 본문 글자 수(사진 제외)가 바뀔 때마다 */
  onTextLengthChange?: (length: number) => void;
}

/** 본문 안 사진의 ✕ 버튼 자리(편집기 왼쪽 위 기준) */
interface ImageMark {
  /** 본문 안 img 순서 */
  index: number;
  top: number;
  right: number;
}

const sameMarks = (a: ImageMark[], b: ImageMark[]): boolean =>
  a.length === b.length && a.every((mark: ImageMark, i: number) => mark.index === b[i].index && mark.top === b[i].top && mark.right === b[i].right);

/**
 * 본문 이미지로 허용하는 형식.
 *
 * PNG 는 올리기 전에 WebP 로 바꿔 용량을 줄인다(utils/compressImage — api.upload.image 가 부른다).
 * GIF 는 압축하면 애니메이션이 깨지고 용량도 커서 받지 않는다.
 */
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ACCEPT_ATTR = ACCEPTED_IMAGE_TYPES.join(',');

/** 허용 형식이 아니면 사유 문구를, 통과면 null 을 돌려준다. */
const rejectReason = (file: File): string | null => {
  if (ACCEPTED_IMAGE_TYPES.includes(file.type)) return null;
  return '지원하지 않는 이미지 형식이에요. JPG·PNG·WebP 로 올려 주세요.';
};

const describeUploadError = (error: any): string => {
  if (error instanceof ImageTooLargeError) return error.message;
  const status = error?.response?.status;
  // 서버가 사유를 내려주면(예: "파일 크기는 10MB를 초과할 수 없습니다.") 그대로 보여주는 게 가장 정확하다
  const serverMessage = error?.response?.data?.message;
  if (serverMessage) return serverMessage;

  if (status === 401 || status === 403) return '로그인이 만료되었습니다. 다시 로그인한 뒤 시도해주세요.';
  if (status === 413) return '이미지 용량이 너무 커서 서버가 거부했습니다. 더 작은 이미지를 사용해주세요.';
  if (status && status >= 500) return '서버 오류로 이미지를 올리지 못했습니다. 잠시 후 다시 시도해주세요.';
  if (error?.message === 'Network Error' || !navigator.onLine) {
    return '네트워크 연결이 불안정합니다. 연결을 확인한 뒤 다시 시도해주세요.';
  }
  return `이미지 업로드에 실패했습니다. (${error?.message ?? '알 수 없는 오류'})`;
};

const BoardContainer = styled.div`
  max-width: 100%;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const AddPostContainer = styled.div`
  height: auto;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
  padding: 10px;
`;

const EditorContainer = styled.div`
  position: relative;

  .ql-editor {
    min-height: 400px;
    font-size: 14px;
    line-height: 1.6;
    /* 어절 중간 줄바꿈 방지(C5) */
    word-break: keep-all;
    overflow-wrap: anywhere;

    /* 폰은 16px — 아이폰이 16px 미만 입력칸을 누르면 화면을 확대한다(UI 가이드 7.1) */
    @media (max-width: 768px) {
      font-size: 16px;
    }
  }

  .ql-toolbar {
    border-top: 1px solid #ccc;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
    border-radius: 8px 8px 0 0;
  }

  .ql-container {
    border-bottom: 1px solid #ccc;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
    border-radius: 0 0 8px 8px;
  }

  .ql-editor:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }

  /* placeholder 스타일 */
  .ql-editor.ql-blank::before {
    font-style: italic;
    color: #a2a2a2;
    left: 15px;
  }

  /* 포커스 시 또는 내용이 있을 때 placeholder 즉시 숨김 */
  .ql-editor:focus::before,
  .ql-editor:not(.ql-blank)::before {
    display: none;
  }
`;

/**
 * 글쓰기 화면용 — 도구 막대·테두리 없이 본문만. 좌우 여백은 바깥(WriteBody)이 준다.
 * 본문 안 사진: 위 6px, 사진 사이 8px.
 */
const BareEditor = styled.div`
  position: relative;
  /* 편집기와 안내 글을 같은 칸에 겹쳐 둔다 — 빈 본문일 때 칸 높이가 안내 글 높이만큼 잡혀 아래 칩 줄과 겹치지 않는다 */
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  cursor: text;

  > .quill {
    grid-area: 1 / 1;
    min-width: 0;
  }

  .ql-container.ql-snow {
    border: none;
    font-family: inherit;
    font-size: 16px;
  }

  .ql-editor {
    /* 두 줄 — 글이 짧으면 아래 칩 줄이 바로 따라온다 */
    min-height: 56px;
    padding: 0;
    font-size: 16px;
    line-height: 1.75;
    color: ${FORM_COLOR.text};
    overflow: visible;
    /* 어절 중간 줄바꿈 방지(C5) — 긴 주소·영문만 칸 안에서 끊는다 */
    word-break: keep-all;
    overflow-wrap: anywhere;
  }

  /* 안내 글은 아래 BarePlaceholder 가 그린다(문단 간격을 맞추려고) */
  .ql-editor.ql-blank::before {
    content: none;
  }

  .ql-editor img {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 6px 0 0;
    border-radius: 10px;
  }

  /* 사진이 이어지면 사이 8px — 한 문단 안에서 이어질 때 / 사진만 있는 문단이 이어질 때 */
  .ql-editor img + img,
  .ql-editor p:has(> img:only-child) + p:has(> img:only-child) > img {
    margin-top: 8px;
  }
`;

const BarePlaceholder = styled.div`
  grid-area: 1 / 1;
  min-width: 0;
  pointer-events: none;
  font-size: 15px;
  line-height: 1.8;
  color: ${FORM_COLOR.placeholder};
  word-break: keep-all;

  p {
    margin: 0 0 12px;
    white-space: pre-line;
  }
  p:last-of-type {
    margin-bottom: 0;
  }
`;

/** 본문 안 사진 오른쪽 위 ✕ — 22px 동그라미 + 14px 아이콘 */
const ImageRemoveButton = styled.button`
  position: absolute;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;

  svg {
    display: block;
    width: 14px;
    height: 14px;
    font-size: 14px;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 8px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid ${designSystem.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.span`
  margin-top: 12px;
  color: #666;
  font-size: 14px;
`;

const Board = forwardRef<BoardHandle, BoardProps>(({ onChange, defaultValue = '', value, onError, uploadPurpose, bare = false, placeholder, onTextLengthChange }: BoardProps, ref) => {
  const [content, setContent] = useState<string>(value || defaultValue || '');
  const [isUploading, setIsUploading] = useState(false);
  const quillRef = useRef<ReactQuill>(null);
  const onChangeRef = useRef(onChange);
  const onErrorRef = useRef(onError);
  // ── bare(글쓰기 화면) 전용 상태 ──
  const bareRef = useRef<HTMLDivElement>(null);
  const onTextLengthChangeRef = useRef(onTextLengthChange);
  onTextLengthChangeRef.current = onTextLengthChange;
  /** 마지막 커서 위치 — 도구 줄의 [사진]을 눌러 포커스가 나가도 그 자리에 사진을 넣는다 */
  const lastRangeRef = useRef<{ index: number; length: number } | null>(null);
  const [blank, setBlank] = useState<boolean>(!(value || defaultValue));
  /** 한글 조합 중 — 첫 글자를 조합하는 동안 안내 글이 글자 밑에 겹쳐 보이지 않게 */
  const [composing, setComposing] = useState<boolean>(false);
  const [imageMarks, setImageMarks] = useState<ImageMark[]>([]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // 파일을 서버에 업로드하고 커서 위치에 이미지 URL 삽입 (툴바 버튼·붙여넣기·드롭 공용)
  const uploadAndInsertImage = useCallback(async (file: File) => {
    // 툴바·붙여넣기·드롭 세 경로가 모두 여기로 모이므로 형식 검사도 여기서 한다
    const reason = rejectReason(file);
    if (reason) {
      if (onErrorRef.current) onErrorRef.current(reason);
      else navigationService.showToast(reason, 'error');
      return;
    }

    setIsUploading(true);
    try {
      // 원본 크기로 미리 거르지 않는다. api.upload.image 가 해상도·화질을 유지한 채 한 번 재인코딩하고,
      // 그래도 한도를 넘으면 요청 없이 ImageTooLargeError 를 던져 아래 catch 에서 바로 안내한다.
      const response = await api.upload.image(file, uploadPurpose ? { purpose: uploadPurpose } : undefined);

      if (response.code === 0 && response.result?.url) {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          // bare: 바깥 버튼으로 올리므로 포커스가 없을 수 있다 — 마지막 커서 자리, 없으면 본문 끝에 넣는다
          const end: number = Math.max(0, quill.getLength() - 1);
          const range = bare
            ? quill.getSelection() ?? { index: Math.min(lastRangeRef.current?.index ?? end, end), length: 0 }
            : quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', response.result.url);
          quill.setSelection(range.index + 1);
        }
      } else {
        throw new Error(`서버 응답 code=${response.code}`);
      }
    } catch (error: any) {
      console.error('이미지 업로드 에러:', error);
      // onError 를 넘기지 않은 화면(이력서 등)도 실패가 묻히지 않게 토스트로 알린다
      const message: string = describeUploadError(error);
      if (onErrorRef.current) onErrorRef.current(message);
      else navigationService.showToast(message, 'error');
    } finally {
      setIsUploading(false);
    }
  }, [uploadPurpose, bare]);

  // 이미지 업로드 핸들러 (툴바 버튼)
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', ACCEPT_ATTR);
    input.click();

    input.onchange = () => {
      const file = input.files?.[0];
      if (file) uploadAndInsertImage(file);
    };
  }, [uploadAndInsertImage]);

  // 붙여넣기/드래그앤드롭 이미지도 base64 인라인 대신 업로드 후 URL 삽입
  // (Quill 기본 동작은 base64로 본문에 인라인해 글 하나가 수백 KB가 됨)
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const root = quill.root;

    const extractImageFiles = (fileList: FileList | undefined | null): File[] =>
      Array.from(fileList ?? []).filter((f) => f.type.startsWith('image/'));

    const handlePaste = (e: ClipboardEvent) => {
      const files = extractImageFiles(e.clipboardData?.files);
      if (files.length === 0) return;
      // Quill 기본 paste 처리(base64 인라인)보다 먼저 가로채야 하므로 전파도 차단
      e.preventDefault();
      e.stopPropagation();
      files.forEach((f) => uploadAndInsertImage(f));
    };

    const handleDrop = (e: DragEvent) => {
      const files = extractImageFiles(e.dataTransfer?.files);
      if (files.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      files.forEach((f) => uploadAndInsertImage(f));
    };

    // Quill이 root(.ql-editor)에 자체 리스너를 먼저 등록하므로,
    // 부모 컨테이너의 캡처 단계에서 가로채야 Quill 기본 동작을 막을 수 있다
    const container: HTMLElement = root.parentElement ?? root;
    container.addEventListener('paste', handlePaste, true);
    container.addEventListener('drop', handleDrop, true);
    return () => {
      container.removeEventListener('paste', handlePaste, true);
      container.removeEventListener('drop', handleDrop, true);
    };
  }, [uploadAndInsertImage]);

  useImperativeHandle(ref, (): BoardHandle => ({
    pickImage: imageHandler,
    focus: () => {
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      quill.focus();
      quill.setSelection(Math.max(0, quill.getLength() - 1), 0);
    },
  }), [imageHandler]);

  /** bare: 본문 안 사진마다 ✕ 버튼 자리를 잰다 */
  const measureImages = useCallback((): void => {
    const quill = quillRef.current?.getEditor();
    const host: HTMLDivElement | null = bareRef.current;
    if (!quill || !host) return;
    const hostRect: DOMRect = host.getBoundingClientRect();
    const next: ImageMark[] = [];
    Array.from(quill.root.querySelectorAll('img')).forEach((img: HTMLImageElement, index: number) => {
      const rect: DOMRect = img.getBoundingClientRect();
      // 아직 불러오지 않은 사진(크기 0)에는 버튼을 두지 않는다 — 다 불러오면 다시 잰다
      if (rect.width === 0 || rect.height === 0) return;
      next.push({ index, top: Math.round(rect.top - hostRect.top), right: Math.round(hostRect.right - rect.right) });
    });
    setImageMarks((previous: ImageMark[]) => (sameMarks(previous, next) ? previous : next));
  }, []);

  const removeImage = useCallback((index: number): void => {
    const quill = quillRef.current?.getEditor();
    const img: HTMLImageElement | undefined = quill?.root.querySelectorAll('img')[index];
    if (!quill || !img) return;
    const blot = Quill.find(img);
    if (!blot || blot instanceof Quill) return;
    quill.deleteText(quill.getIndex(blot), 1, 'user');
  }, []);

  // bare: 커서 자리 기억 · 한글 조합 표시 · 사진이 불러와지거나 폭이 바뀌면 ✕ 자리 다시 재기
  useEffect(() => {
    if (!bare) return undefined;
    const quill = quillRef.current?.getEditor();
    if (!quill) return undefined;
    const root: HTMLElement = quill.root;
    const onSelection = (range: { index: number; length: number } | null): void => {
      if (range) lastRangeRef.current = range;
    };
    const onCompositionStart = (): void => setComposing(true);
    const onCompositionEnd = (): void => setComposing(false);
    quill.on('selection-change', onSelection);
    root.addEventListener('compositionstart', onCompositionStart);
    root.addEventListener('compositionend', onCompositionEnd);
    // load 는 위로 전달되지 않아 capture 로 듣는다
    root.addEventListener('load', measureImages, true);
    const observer: ResizeObserver | null = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measureImages) : null;
    observer?.observe(root);
    return () => {
      quill.off('selection-change', onSelection);
      root.removeEventListener('compositionstart', onCompositionStart);
      root.removeEventListener('compositionend', onCompositionEnd);
      root.removeEventListener('load', measureImages, true);
      observer?.disconnect();
    };
  }, [bare, measureImages]);

  // bare: 내용이 바뀔 때마다 빈 본문 여부·글자 수·사진 자리를 다시 본다
  useEffect(() => {
    if (!bare) return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    setBlank(quill.getLength() <= 1);
    onTextLengthChangeRef.current?.(quill.getText().replace(/\n$/, '').length);
    measureImages();
  }, [bare, content, measureImages]);

  const modules = useMemo(() => (bare ? { toolbar: false } : {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
  }), [imageHandler, bare]);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align',
    'list',
    'blockquote', 'code-block',
    'link', 'image'
  ];

  useEffect(() => {
    if (value !== undefined && value !== content) {
      setContent(value);
    }
  }, [value]);

  useEffect(() => {
    if (defaultValue && !content) {
      setContent(defaultValue);
    }
  }, [defaultValue]);

  const handleChange = (value: string) => {
    setContent(value);
    onChangeRef.current(value);
  };

  if (bare) {
    return (
      <BareEditor
        ref={bareRef}
        data-write-editor=""
        // 안내 글 아래처럼 편집기 밖 빈 자리를 눌러도 본문에 커서가 가게
        onMouseDown={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest('.ql-editor') || target.closest('button')) return;
          event.preventDefault();
          const quill = quillRef.current?.getEditor();
          if (!quill) return;
          quill.focus();
          quill.setSelection(Math.max(0, quill.getLength() - 1), 0);
        }}
      >
        {isUploading && (
          <LoadingOverlay>
            <Spinner />
            <LoadingText>이미지 업로드 중...</LoadingText>
          </LoadingOverlay>
        )}
        <ReactQuill
          ref={quillRef}
          value={content}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          theme="snow"
        />
        {blank && !composing && placeholder && (
          <BarePlaceholder aria-hidden="true">
            {placeholder.split('\n\n').map((paragraph: string) => <p key={paragraph}>{paragraph}</p>)}
          </BarePlaceholder>
        )}
        {imageMarks.map((mark: ImageMark) => (
          <ImageRemoveButton
            key={mark.index}
            type="button"
            aria-label="사진 빼기"
            style={{ top: mark.top + 6, right: mark.right + 6 }}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => removeImage(mark.index)}
          >
            <CloseRoundedIcon aria-hidden="true" />
          </ImageRemoveButton>
        ))}
      </BareEditor>
    );
  }

  return (
    <BoardContainer>
      <AddPostContainer>
        <EditorContainer>
          {isUploading && (
            <LoadingOverlay>
              <Spinner />
              <LoadingText>이미지 업로드 중...</LoadingText>
            </LoadingOverlay>
          )}
          <ReactQuill
            ref={quillRef}
            value={content}
            onChange={handleChange}
            modules={modules}
            formats={formats}
            placeholder="내용을 입력하세요..."
            theme="snow"
          />
        </EditorContainer>
      </AddPostContainer>
    </BoardContainer>
  );
});

Board.displayName = 'Board';

export default Board;
